import { authApi } from '@/lib/api/auth.api'
import { announceSignOut } from '@/lib/api/request-scope'
import { logoutSession } from '@/lib/auth/logout-session'
import { createLogger } from '@/lib/logger'
import { clearAppData, isTauri } from '@/lib/utils/tauri'
import { resetPersistedModuleFilters } from '@/store/filter-scope'
import { toast } from 'sonner'

import { SessionChangedError } from '@scrambl/shared/session-scope'

const logger = createLogger('utils/auth/auth-utils')

async function performAccountDeletionCleanup() {
  try {
    // Call the auth API to logout
    await authApi.logout()
  } catch (error) {
    logger.error('[Auth] Error during logout:', error)
  } finally {
    // Dock badge and tray timer are OS-level state that outlives the webview,
    // so clear them before the hard reload below tears the app down.
    if (isTauri()) {
      try {
        const [{ browserNotificationService }, tauriModule] = await Promise.all(
          [
            import('@/services/browserNotificationService'),
            import('@/lib/utils/tauri'),
          ],
        )
        await browserNotificationService.clearBadgeCount()
        await tauriModule.setTrayTitle(null)
      } catch (desktopChromeError) {
        logger.error(
          '[Auth] Failed to clear desktop badge/tray state:',
          desktopChromeError,
        )
      }
    }

    // Account deletion intentionally purges local data even when the server
    // session can no longer authenticate (the account has already been deleted).
    await clearAppData()

    // Other tabs of this profile still hold this account in memory; tell
    // them the cookie is gone so they drop it instead of fetching (and
    // persisting) the next account's data under this one.
    announceSignOut()

    // A hard replacement resets all in-memory stores and query caches.
    window.location.replace('/login')
  }
}

/** Standard sign-out that preserves unrelated device preferences/accounts. */
export async function handleLogout() {
  try {
    const completion = await logoutSession()
    completion.assertCurrent()
    if (isTauri()) {
      try {
        const [{ browserNotificationService }, tauriModule] = await Promise.all(
          [
            import('@/services/browserNotificationService'),
            import('@/lib/utils/tauri'),
          ],
        )
        completion.assertCurrent()
        await browserNotificationService.clearBadgeCount()
        completion.assertCurrent()
        await tauriModule.setTrayTitle(null)
      } catch (error) {
        if (error instanceof SessionChangedError) throw error
        logger.error('[Auth] Failed to clear desktop badge/tray state:', error)
      }
      completion.assertCurrent()
    }
    resetPersistedModuleFilters()
    const keys = [
      'currentOrganizationId',
      'currentUserId',
      'pendingInviteToken',
      'invitedOrganizationName',
      'scrambl-login-session',
      'organization-store',
      'organization-invite-store',
      'user-profile-storage',
    ]
    Object.keys(localStorage).forEach(key => {
      if (keys.includes(key) || key.startsWith('user-profile-image-'))
        localStorage.removeItem(key)
    })
    sessionStorage.clear()
    announceSignOut()
    window.location.replace('/login')
  } catch (error) {
    if (error instanceof SessionChangedError) return
    logger.error('[Auth] Sign out could not be completed:', error)
    toast.error('Sign out could not be completed. Please try again.', {
      action: { label: 'Retry', onClick: () => void handleLogout() },
    })
  }
}

/**
 * Destructive sign-out used after account deletion. Removes every Scrambl
 * credential, cache, and persisted record on this device before reloading.
 */
export async function handleLogoutAndPurge() {
  await performAccountDeletionCleanup()
}
